// Pure helpers for the V3 message views (M17, M77–M81, M119); no network, no storage.
import { own, text, contactGroups } from "./memberDerive";
import { shortName } from "./v3People";

const idOf = (v) => (typeof v === "string" && v) || (Number.isSafeInteger(v) ? String(v) : "");
const clean = (v) => (typeof v === "string" ? v.trim() : "");

export function findConv(list, id) {
  if (!Array.isArray(list)) return null;
  return list.find((c) => c && typeof c === "object" && idOf(own(c, "id")) && idOf(own(c, "id")) === String(id)) || null;
}

// Which view /nachrichten/:id shows; pending direct requests belong to the contact views (M54/M55).
export function chatRoute(c) {
  if (!c) return "missing";
  const type = own(c, "type");
  if (type === "direct") {
    const s = own(c, "status");
    if (s === "accepted") return own(own(c, "other"), "id") ? "chat" : "missing";
    if (s === "pending") return own(c, "is_requester") === true ? "outgoing" : "incoming";
    return "missing";
  }
  return type === "group" || type === "broadcast" ? "chat" : "missing";
}

// M17/M77 lists: confirmed direct chats and circle chats; open requests are only counted.
export function chatLists(conversations) {
  const list = Array.isArray(conversations) ? conversations.filter((c) => c && typeof c === "object") : [];
  const g = contactGroups(list);
  return {
    direct: g.accepted.filter((c) => chatRoute(c) === "chat"),
    groups: list.filter((c) => own(c, "type") !== "direct" && chatRoute(c) === "chat"),
    incoming: g.incoming.length,
  };
}

export const isDirect = (c) => own(c, "type") === "direct";
export const convTitle = (c) => (isDirect(c) ? shortName(text(own(own(c, "other"), "name"))) || "Kontakt" : text(own(c, "title")).trim() || "Kreisgespräch");
export const convContext = (c) => (isDirect(c) ? "Bestätigter Kontakt" : own(c, "type") === "broadcast" ? "Kanal des VISIBILIS-Teams" : "Kreisgespräch");
export const lastText = (c) => text(own(own(c, "last_message"), "body")).trim();
export const chatHref = (c, rest = "") => `/nachrichten/${encodeURIComponent(c.id)}${rest}`;

export function convIntro(c) {
  if (isDirect(c)) {
    const first = convTitle(c).split(" ")[0];
    return `Dieses Gespräch ist mit deinem bestätigten Kontakt ${first}.`;
  }
  return own(c, "type") === "broadcast" ? "Dies ist ein Kanal des VISIBILIS-Teams." : `Dieses Gespräch gehört zum Kreis „${convTitle(c)}“.`;
}

function message(m, mine) {
  if (!m || typeof m !== "object") return null;
  const id = idOf(own(m, "id"));
  const body = text(own(m, "body"));
  if (!id || !body.trim()) return null;
  return { id, body, mine: mine || own(m, "is_me") === true, sender: clean(own(m, "sender_name")), senderId: idOf(own(m, "sender_member_id")) };
}

// GET /member/conversations/{id}/messages → messages[]; invalid entries are dropped, a broken shape is null.
export function normalizeMessages(d) {
  const list = own(d, "messages");
  return Array.isArray(list) ? list.map((m) => message(m, false)).filter(Boolean) : null;
}

export const sentMessage = (d) => message(own(d, "message"), true);

// Label above a bubble: the own first name or the sender's short name (M17).
export function senderLabel(m, c, ownName) {
  if (m.mine) return shortName(ownName).split(" ")[0] || "Du";
  if (isDirect(c)) return convTitle(c).split(" ")[0];
  return shortName(m.sender) || "Mitglied";
}

// Reporting needs the sender; in a direct chat that is the other person (M80).
export function reportTarget(c, m) {
  if (!m || m.mine) return "";
  return isDirect(c) ? idOf(own(own(c, "other"), "id")) : m.senderId;
}

export const REPORT_REASONS = ["spam", "harassment", "inappropriate", "fake", "other"];

export function reportPayload(c, m, reason) {
  const target = reportTarget(c, m);
  if (!target || !REPORT_REASONS.includes(reason)) return null;
  return { reported_member_id: target, reason, conversation_id: String(c.id), message_id: m.id };
}

// After a failed POST: only an unanswered or unconfirmed send is re-checked against the thread.
export const sendNeedsCheck = (r) => !sentMessage(own(r, "data")) && !(typeof own(r, "rejected") === "string" && own(r, "rejected"));

const arrived = (list, before, body) => (Array.isArray(list) ? list.find((m) => m.mine && !before.includes(m.id) && m.body.trim() === body) : null);

// Send result → sent | forbidden | gone | failure (M119). Success needs the Core message or a fresh thread read.
export function sendOutcome(r, reread, before, body) {
  const msg = sentMessage(own(r, "data"));
  if (msg) return { state: "sent", message: msg };
  const status = own(r, "status");
  if (status === 403) return { state: "forbidden" };
  if (status === 404) return { state: "gone" };
  const ok = reread && reread.ok === true;
  if (ok && arrived(reread.list, before, body)) return { state: "sent", list: reread.list };
  const fail = (kind) => ({ state: "failed", kind, before, body, detail: kind === "rejected" ? own(r, "rejected") : "" });
  if (typeof own(r, "rejected") === "string" && own(r, "rejected")) return fail(status === 429 ? "rate" : "rejected");
  return fail(ok && own(r, "data") == null ? "not_sent" : "unclear");
}

export const FAIL_TEXT = {
  not_sent: { banner: "Nicht gesendet", status: "Nicht gesendet", next: "Verbindung prüfen und bewusst erneut senden" },
  rate: { banner: "Nicht gesendet", status: "Nicht gesendet · zu viele Nachrichten in kurzer Zeit", next: "Kurz warten und bewusst erneut senden" },
  rejected: { banner: "Nicht gesendet", status: "Nicht gesendet · nicht angenommen", next: "Hinweis lesen, Text prüfen und bewusst erneut senden" },
  unclear: { banner: "Status unklar", status: "Unklar · Verbindung unterbrochen", next: "Verbindung prüfen und den Verlauf prüfen, bevor du erneut sendest" },
};

// GET /member/conversations/{id}/media (proposed, D-60): only safe image addresses, alt text required.
export function safeMediaUrl(u) {
  if (typeof u !== "string") return "";
  const v = u.trim();
  return /^https:\/\/[^\s]+$/i.test(v) || (/^\/[^/\\\s]/.test(v) && !/^\/\//.test(v)) ? v : "";
}

export function normalizeMedia(d) {
  const list = own(d, "media");
  if (!Array.isArray(list)) return null;
  return list.map((m) => {
    if (!m || typeof m !== "object") return null;
    const id = idOf(own(m, "id"));
    const url = safeMediaUrl(own(m, "url"));
    const alt = clean(own(m, "alt_text"));
    return id && url && alt ? { id, url, alt, title: clean(own(m, "title")) || "Bild" } : null;
  }).filter(Boolean);
}

const cpLen = (v) => Array.from(String(v ?? "").trim()).length;
export const ALT_MAX = 250;

// M79: a picked image is only checked here; nothing is uploaded (D-10, D-61).
export function attachmentErrors({ file, alt }) {
  const e = {};
  if (!file) e.file = "Bitte wähle ein Bild aus.";
  else if (typeof file.type !== "string" || !/^image\/(jpeg|png|webp|gif)$/.test(file.type)) e.file = "Bitte wähle eine Bilddatei im Format JPG, PNG, WebP oder GIF.";
  if (!cpLen(alt)) e.alt = "Bitte beschreibe kurz, was auf dem Bild zu sehen ist.";
  else if (cpLen(alt) > ALT_MAX) e.alt = `Die Bildbeschreibung darf höchstens ${ALT_MAX} Zeichen lang sein.`;
  return e;
}
